/** As the packlist API sends it (module `packlists`; the web app does not import server modules). */
export type Source = 'manual' | 'kit' | 'block' | 'template' | 'rule' | 'quote' | 'inputlist';
/**
 * Review 6 (owner 2026-10-24 15:35, „zajętość dłuższa”): the days a line reserves — its own days widened by its
 * „Dni rezerwacji…” (absent when the role may not see the days).
 */
export function reservedSpan(line: Pick<LineView, 'firstDay' | 'lastDay' | 'extraHeld'>): { firstDay: string | undefined; lastDay: string | undefined } {
  if (!line.firstDay || !line.lastDay) return { firstDay: undefined, lastDay: undefined };
  const days = [line.firstDay, line.lastDay, ...(line.extraHeld ?? [])].sort();
  return { firstDay: days[0], lastDay: days.at(-1) };
}

export type LineView = {
  id: string;
  itemId: string;
  itemName: string;
  kind: 'serialized' | 'bulk' | 'consumable' | null;
  categoryId: string | null;
  /** The line's window (always sent): lines of the same item and window are duplicates ("powtórzenia"). */
  windowStart: string;
  windowEnd: string;
  // Fields with permissions: absent for a role that may not see them.
  quantity?: number;
  firstDay?: string;
  lastDay?: string;
  followsEvent?: boolean;
  notes?: string;
  /**
   * „Dni rezerwacji…” (owner 2026-10-24 15:29): the event's extra days of the item (null: none, or a line with its own
   * days), the days they hold beyond the line's own, and the setting's version (0: never set).
   */
  extraDays?: { before: boolean; after: boolean; dates: string[] } | null;
  extraHeld?: string[];
  extraDaysVersion?: number;
  loadedQty?: number;
  returnedQty?: number;
  version: number;
  /** Units allocated to the line's hard reservation (serialized items). */
  units: { id: string; serialNumber: string | null; internalCode: string }[];
  damageReports: { id: string; unitId: string | null; description: string; fileIds: string[]; createdAt: string }[];
  contributions: { source: Source; sourceId: string | null; quantity: number; overridden: boolean }[];
  reservation: { kind: 'soft' | 'hard' | null; hardFailed: boolean };
  availability: {
    free: number;
    missing: number;
    /** F28: how much of the line gear rented for this event covers ("w tym dopożyczone N"). */
    inbound?: number;
    /** `block`: an equipment block (owner 2026-10-24 15:29). */
    busyIn: { groupId: string | null; groupLabel: string; kind: 'soft' | 'hard'; quantity: number; start: string; end: string; block?: true }[];
  };
};
/**
 * Owner 2026-11-04: „Pozycja własna” — a line from outside the catalogs (no item, no reservation). `quantity` hidden with
 * the lines' „Ilość”. No price (owner 2026-11-04 22:36: given in the quote only).
 */
export type CustomLineView = { id: string; name: string; quantity?: number; /** owner 2026-11-04 19:30; null/absent: „Bez kategorii” */ categoryId?: string | null; version: number };
export type NotReturned = { lineId: string; itemId: string; itemName: string; missing: number };
/** What a rule proposes now (module rules); `quantity` hidden with the lines' "Ilość". */
export type RuleOutcome = { ruleId: string; ruleName: string; hardness: 'required' | 'suggested' | 'reminder'; itemId: string | null; quantity?: number; message: string; itemName?: string };
/**
 * Gear from a supplier on the packlist; `quantity` hidden with the lines' "Ilość". Owner 2026-10-24 („ma być jak w
 * kosztorysie”): shown in the category group of `categoryId` (its position's), else of `sectionCategoryId` (the quote
 * section it came from), else with our uncategorised gear (both absent from older servers / test data).
 */
export type SupplierLine = {
  itemId: string | null;
  name: string;
  quantity?: number;
  /** The quote line it came from (its supplier is changed there). */
  quoteLineId: string | null;
  /** Owner 2026-10-24 20:36: the opaque id the line's × is sent with (none in an older version's copy). */
  id?: string | null;
  categoryId?: string | null;
  sectionCategoryId?: string | null;
  /**
   * Owner 2026-11-01: its rental's event is closed — `anulowane`: cancelled with it (struck through, red tag);
   * `sprawdz`: picked up before („… – sprawdź rozliczenie z dostawcą”). Absent: none.
   */
  rentalMark?: { kind: 'anulowane' | 'sprawdz'; label: string };
};
export type SupplierSection = { supplierId: string; supplierName: string; lines: SupplierLine[] };
/** A version of the packlist (owner 2026-10-22): `label` e.g. "z kosztorysu v2", "przywrócona v1" ('' for the first). */
export type PacklistVersion = { number: number; current: boolean; label: string; createdAt: string | null };
/** An older version as `GET /api/packlists/:eventId/versions/:number` sends it: read-only lines and suppliers. */
export type VersionLine = Pick<LineView, 'id' | 'itemId' | 'itemName' | 'categoryId' | 'quantity' | 'firstDay' | 'lastDay' | 'followsEvent' | 'notes' | 'contributions'>;
export type VersionView = { number: number; label: string; current: boolean; lines: VersionLine[]; suppliers: SupplierSection[]; customLines?: CustomLineView[] };

export type PacklistView = {
  /** `reserveDaysBefore/After`: the event's "Rezerwuj sprzęt także" days (owner 06:47), when the role sees them. */
  event: { id: string; name: string; reserveDaysBefore?: number; reserveDaysAfter?: number };
  lines: LineView[];
  /** Owner 2026-11-04 (absent from older servers / test data: none). */
  customLines?: CustomLineView[];
  returnClosedAt: string | null;
  notReturned: NotReturned[] | null;
  /** Suggested rules not on the list yet ("Dodaj"), reminders, what each required/suggested rule proposes, rule names by id. */
  suggestions?: RuleOutcome[];
  reminders?: RuleOutcome[];
  ruleOutcomes?: RuleOutcome[];
  ruleNames?: Record<string, string>;
  /** Owner 2026-10-22 14:29 (absent from older servers / test data: none). */
  suppliers?: SupplierSection[];
  /** Review I2: a pickup already picked up differs from the quote ("Uzupełnij odbiory"). */
  suppliersDiffer?: boolean;
  /** Review M4: false — nothing keeps the pickups in step (the quote was deleted). */
  suppliersFollow?: boolean;
  versions?: PacklistVersion[];
  currentVersion?: number;
};

/** The required rules that put something on the line (removing it needs "Pomiń regułę"). */
export function requiredRulesOf(line: Pick<LineView, 'contributions'>, outcomes: readonly RuleOutcome[]): string[] {
  const required = new Set(outcomes.filter((o) => o.hardness === 'required').map((o) => o.ruleId));
  return line.contributions.flatMap((c) => (c.source === 'rule' && c.sourceId && required.has(c.sourceId) ? [c.sourceId] : []));
}

/** "A-01 / SN123": how a unit is named on the phone screens. */
export const unitLabel = (u: { serialNumber: string | null; internalCode: string }) => [u.internalCode, u.serialNumber].filter(Boolean).join(' / ') || 'egzemplarz bez numeru';

export const PACKLISTS_RESOURCE = 'packlists.lists';
export const packlistKey = (eventId: string) => ['packlists', eventId] as const;
export const MAX_QUANTITY = 1_000_000;

const DAY = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'long', year: 'numeric' });
/** Days arrive as 'YYYY-MM-DD'; noon UTC is the same day in Warsaw. */
const fmt = (day: string) => DAY.format(new Date(`${day}T12:00:00Z`));
export function formatWindow(line: Pick<LineView, 'firstDay' | 'lastDay' | 'followsEvent'>): string {
  if (line.followsEvent) return 'całe wydarzenie';
  if (!line.firstDay || !line.lastDay) return '—';
  return line.firstDay === line.lastDay ? fmt(line.firstDay) : `${fmt(line.firstDay)} – ${fmt(line.lastDay)}`;
}

export const kindLabel = (kind: 'soft' | 'hard') => (kind === 'hard' ? 'pewna' : 'wstępna');

/** Window change for PATCH: when either day changed both are sent (the server needs the pair); otherwise null. */
export function windowChanges(line: Pick<LineView, 'firstDay' | 'lastDay'>, firstDay: string, lastDay: string): { firstDay: string; lastDay: string } | null {
  return firstDay !== line.firstDay || lastDay !== line.lastDay ? { firstDay, lastDay } : null;
}

/** Lines of the same item and window ("powtórzenia"), oldest first: the server keeps that order. */
export const duplicateKey = (l: Pick<LineView, 'itemId' | 'windowStart' | 'windowEnd'>) => `${l.itemId}|${l.windowStart}|${l.windowEnd}`;

/** Every item and window standing on more than one line, in list order. */
export function duplicateGroups(lines: readonly LineView[]): LineView[][] {
  const groups = new Map<string, LineView[]>();
  for (const l of lines) groups.set(duplicateKey(l), [...(groups.get(duplicateKey(l)) ?? []), l]);
  return [...groups.values()].filter((g) => g.length > 1);
}

/** Notes of merged lines as the server joins them: the different, non-empty ones, "; " between. */
export const joinNotes = (notes: readonly (string | undefined)[]) => [...new Set(notes.map((n) => (n ?? '').trim()).filter((n) => n !== ''))].join('; ');

/** One row of the merge preview: "Kabel XLR 10 m: 8 + 16 → 24; uwagi: FOH; monitory". */
export function mergePreview(group: readonly LineView[]): string {
  const first = group[0]!;
  const window = first.followsEvent === false ? ` (${formatWindow(first)})` : '';
  const quantities = group.map((l) => l.quantity);
  const sum = quantities.every((q): q is number => q !== undefined)
    ? `${quantities.join(' + ')} → ${quantities.reduce((a, b) => a + b, 0)}`
    : `${group.length} pozycje → 1`;
  const notes = joinNotes(group.map((l) => l.notes));
  return `${first.itemName}${window}: ${sum}${notes ? `; uwagi: ${notes}` : ''}`;
}

/** "ręcznie 2 + zestaw Monitory 4 + blok Mikrofony 8 + reguła Statywy 3"; `sourceNames`: names of kits, blocks, templates and rules by id. */
export function formatSources(line: LineView, sourceNames: Map<string, string>): string {
  const named = (word: string, c: LineView['contributions'][number]) => `${word} ${(c.sourceId && sourceNames.get(c.sourceId)) || ''} ${c.quantity}`.replace('  ', ' ');
  const label = (c: LineView['contributions'][number]) => {
    switch (c.source) {
      case 'manual':
        return `ręcznie ${c.quantity}`;
      case 'kit':
        return named('zestaw', c);
      case 'block':
        return named('blok', c);
      case 'template':
        return named('szablon', c);
      case 'rule':
        return named('reguła', c);
      case 'quote':
        return `kosztorys ${c.quantity}`;
      case 'inputlist':
        return `inputlista ${c.quantity}`;
    }
  };
  const order = { manual: 0, kit: 1, block: 2, template: 3, quote: 4, inputlist: 5, rule: 6 } as const;
  return [...line.contributions].sort((a, b) => order[a.source] - order[b.source]).map(label).join(' + ');
}

export function reservationText(line: LineView): string {
  const { kind, hardFailed } = line.reservation;
  if (kind === null) return 'brak rezerwacji';
  if (kind === 'soft' && hardFailed) return 'pewna nieudana';
  return kindLabel(kind);
}


/**
 * Owner 2026-11-04 19:30: „Pozycje własne” (an older version's) by category — the catalog's order (`categories`, as
 * `categoryLabels` gives them), „Bez kategorii” (none, or a category gone) last; by name inside.
 */
export function customByCategory(lines: readonly CustomLineView[], categories: ReadonlyArray<{ id: string; label: string }>): Array<{ label: string; lines: CustomLineView[] }> {
  const known = new Map(categories.map((c, i) => [c.id, i]));
  const groups = new Map<string, CustomLineView[]>();
  for (const l of lines) {
    const key = l.categoryId && known.has(l.categoryId) ? l.categoryId : '';
    groups.set(key, [...(groups.get(key) ?? []), l]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === '' ? Infinity : known.get(a)!) - (b === '' ? Infinity : known.get(b)!))
    .map(([key, ls]) => ({ label: key === '' ? 'Bez kategorii' : categories[known.get(key)!]!.label, lines: [...ls].sort((x, y) => x.name.localeCompare(y.name, 'pl')) }));
}
